import { Chart } from "chart.js";
import { Link, useNavigate } from "react-router-dom";
import { FC, useEffect } from "react"

import { absolutePath } from "../../../../../helpers/relativePath";
import { TASK_LIST } from "../../../../../helpers/routes";
import { taskOverview } from "app/reducers/task/taskDataInterface";
import { ASSIGNED, IN_PROGRESS, SENT_FOR_CHECK, APPROVED, ON_HOLD, TAX_PENDING, COMPLETED, PENDING_FROM_CLIENT } from "app/helpers/constants";

interface props {
    taskOverview: taskOverview
}

const displayChart = (taskOverview: taskOverview) => {

    let element: any = document.getElementById("task_overview");

    let config: any = {
        type: 'doughnut',
        data: {
            datasets: [{
                data: [taskOverview.AssignedTaskCount, taskOverview.InProgressTaskCount, taskOverview.SendForCheckTaskCount, taskOverview.ApprovedTaskCount, taskOverview.OnHoldTaskCount, taskOverview.TaxPendingTaskCount, taskOverview.CompletedTaskCount, taskOverview.PendingFromClientTaskCount],
                backgroundColor: ['#00a3ff', '#ffc700', '#B5B5C3', '#50cd89', '#f1416c', '#9269e7', '#50CDa8', '#dd795e']
            }],
            labels: ['Assigned', 'In Progress', 'Send For Check', 'Approved', 'On Hold', 'Tax Pending', 'Completed/Filed', 'Pending From Client']
        },
        options: {
            chart: {
                fontFamily: 'inherit'
            },
            cutoutPercentage: 75,
            responsive: true,
            maintainAspectRatio: false,
            cutout: '75%',
            title: {
                display: false
            },
            animation: {
                animateScale: true,
                animateRotate: true
            },
            tooltips: {
                enabled: true,
                intersect: true,
                mode: 'nearest',
                bodySpacing: 10,
                yPadding: 10,
                xPadding: 10,
                caretPadding: 0,
                displayColors: false,
                backgroundColor: '#20D489',
                titleFontColor: '#ffffff',
                cornerRadius: 4,
                footerSpacing: 0,
                titleSpacing: 0
            },
            plugins: {
                legend: {
                    display: false
                }
            }
        }
    };
    if (element) {
        let ctx = element.getContext('2d');
        let myDoughnut = new Chart(ctx, config);
        return myDoughnut;
    }
}

const AdminPieChart : FC<props> = ({taskOverview}) => {
    const navigate = useNavigate();

    const handleStatusClick = (statusId: number) => {
        navigate(`${absolutePath(TASK_LIST)}?PageNumber=1&Filters=Status%3D${statusId}`);
    };

    useEffect(() => {
        let myChart = displayChart(taskOverview);

        return () => {
            if (myChart) {
                myChart.destroy()
            }
        }
    }, [])

    return (
        <div className="col-lg-4 mb-8">
            <div className="card card-flush h-lg-100">
                <div className="card-header pt-5">
                    <div className="card-title flex-column">
                        <h3 className="fw-bold mb-1">Tasks Summary</h3>
                        {/* <div className="fs-6 fw-semibold text-gray-400"></div> */}
                    </div>
                    <div className="card-toolbar">
                        <Link to={absolutePath(TASK_LIST)} className="btn btn-light btn-sm btn-active-light-primary">View Tasks</Link>
                    </div>
                </div>
                <div className="card-body p-9 pt-5">
                    <div className="d-flex flex-wrap">
                        <div className="position-relative d-flex flex-center h-175px w-175px me-15 mb-7">
                            <div className="position-absolute translate-middle start-50 top-50 d-flex flex-column flex-center">
                                <span className="fs-2qx fw-bold">{taskOverview.AllTaskCount}</span>
                                <span className="fs-6 fw-semibold text-gray-400">Total Tasks</span>
                            </div>
                            <canvas id="task_overview"></canvas>
                        </div>
                        <div className="d-flex flex-column justify-content-center flex-row-fluid mb-5">
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(ASSIGNED.id)}>
                                <div className="bullet bg-primary me-3"></div>
                                <div className="text-gray-400">Assigned</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.AssignedTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(IN_PROGRESS.id)}>
                                <div className="bullet bg-warning me-3"></div>
                                <div className="text-gray-400">In Progress</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.InProgressTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(SENT_FOR_CHECK.id)}>
                                <div className="bullet bg-gray-400 me-3"></div>
                                <div className="text-gray-400">Send For Check</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.SendForCheckTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(APPROVED.id)}>
                                <div className="bullet bg-success me-3"></div>
                                <div className="text-gray-400">Approved</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.ApprovedTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(ON_HOLD.id)}>
                                <div className="bullet bg-danger me-3"></div>
                                <div className="text-gray-400">On Hold</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.OnHoldTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(TAX_PENDING.id)}>
                                <div className="bullet bg-info me-3"></div>
                                <div className="text-gray-400">Tax Pending</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.TaxPendingTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center mb-3 w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(COMPLETED.id)}>
                                <div className="bullet bg-completed-task me-3"></div>
                                <div className="text-gray-400">Completed/Filed</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.CompletedTaskCount}</div>
                            </div>
                            <div className="d-flex fs-6 fw-semibold align-items-center w-200px w-lg-200px cursor-pointer" onClick={() => handleStatusClick(PENDING_FROM_CLIENT.id)}>
                                <div className="bullet bg-pending-from-client-task me-3"></div>
                                <div className="text-gray-400">Pending From Client</div>
                                <div className="ms-auto fw-bold text-gray-700">{taskOverview.PendingFromClientTaskCount}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminPieChart